import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlus, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useDeleteCompanyLogo, useUploadCompanyLogo } from '@/services/api/endpoints/CompanyService';
import type { Company } from '@/types';

/** Same limits as the API (`POST /admin/companies/:id/logo`); the API re-checks by magic bytes and re-encodes. */
const MAX_BYTES = 1024 * 1024;
const ACCEPT = 'image/png,image/jpeg,image/webp';

type LocalCheck = 'ok' | 'type' | 'svg' | 'size';

/** Signature check on the first bytes, so a renamed SVG / GIF fails here and not after the upload. */
async function checkFile(file: File): Promise<LocalCheck> {
  if (file.size > MAX_BYTES) return 'size';
  if (file.type === 'image/svg+xml' || /\.svg$/i.test(file.name)) return 'svg';
  const b = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const ascii = (from: number, to: number) => String.fromCharCode(...b.slice(from, to));
  const isPng = b[0] === 0x89 && ascii(1, 4) === 'PNG';
  const isJpeg = b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff;
  const isWebp = ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP';
  if (isPng || isJpeg || isWebp) return 'ok';
  return ascii(0, 5).trimStart().startsWith('<') ? 'svg' : 'type';
}

const SERVER_ERRORS: Record<string, string> = {
  LOGO_UNSUPPORTED_TYPE: 'type',
  LOGO_SVG_NOT_ALLOWED: 'svg',
  LOGO_TOO_LARGE: 'size',
  LOGO_INVALID_IMAGE: 'invalid',
  LOGO_STORAGE_UNAVAILABLE: 'storage',
};

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || '?';
}

/**
 * VP-LOGO: the operator logo the VendPay app shows (wallet card plate, machine list / sheet). A white tile as the app
 * draws it, plus the wallet-card plate at its real size. Upload / Replace saves at once (the API audits each change);
 * Remove asks for an inline confirmation first.
 */
export function CompanyLogoSection({ company }: { company: Company }) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadCompanyLogo();
  const remove = useDeleteCompanyLogo();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const busy = upload.isPending || remove.isPending;
  const logoUrl = company.logoUrl || null;

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const check = await checkFile(file);
    if (check !== 'ok') {
      toast.error(t(`companies.logo.errors.${check}`));
      return;
    }
    try {
      await upload.mutateAsync({ id: company.id, file });
      toast.success(t('companies.logo.uploadSuccess'));
    } catch (err: any) {
      const key = SERVER_ERRORS[err?.response?.data?.error?.code];
      toast.error(key ? t(`companies.logo.errors.${key}`) : t('companies.logo.uploadError'));
    }
  };

  const handleRemove = async () => {
    try {
      await remove.mutateAsync(company.id);
      toast.success(t('companies.logo.removeSuccess'));
    } catch {
      toast.error(t('companies.logo.removeError'));
    } finally {
      setConfirmRemove(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('companies.logo.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="w-24 h-24 shrink-0 rounded-xl border bg-white shadow-sm flex items-center justify-center overflow-hidden">
            {logoUrl ? (
              <img src={logoUrl} alt={company.name} className="w-[72%] h-[72%] object-contain" />
            ) : (
              <span className="w-12 h-12 rounded-lg bg-primary/10 text-primary font-bold flex items-center justify-center">
                {initials(company.name)}
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0 space-y-3">
            <p className="text-sm text-muted-foreground">{t('companies.logo.description')}</p>
            <div className="flex flex-wrap gap-2">
              <input ref={inputRef} type="file" accept={ACCEPT} onChange={handleFile} className="hidden" disabled={busy} />
              <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy} isLoading={upload.isPending}>
                {logoUrl ? <Upload className="w-4 h-4 mr-2" /> : <ImagePlus className="w-4 h-4 mr-2" />}
                {upload.isPending ? t('companies.logo.uploading') : logoUrl ? t('companies.logo.replace') : t('companies.logo.upload')}
              </Button>
              {logoUrl && (
                <Button type="button" variant="outline" size="sm" onClick={() => setConfirmRemove(true)} disabled={busy || confirmRemove}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('companies.logo.remove')}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{t('companies.logo.hint')}</p>
          </div>
        </div>

        {confirmRemove && (
          <div className="rounded-md bg-muted/50 p-3 space-y-3">
            <p className="text-sm">{t('companies.logo.confirmRemove', { name: company.name })}</p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setConfirmRemove(false)} disabled={remove.isPending}>
                {t('common.cancel')}
              </Button>
              <Button variant="destructive" size="sm" onClick={handleRemove} isLoading={remove.isPending}>
                {t('companies.logo.remove')}
              </Button>
            </div>
          </div>
        )}

        {/* The wallet-card plate at the app's size (30 px pill, logo box 46×22) */}
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">{t('companies.logo.appPreview')}</p>
          <div className="rounded-xl bg-slate-800 p-3">
            <div className="inline-flex max-w-full h-[30px] items-center gap-[7px] rounded-lg bg-white pl-1.5 pr-2.5 shadow-sm">
              {logoUrl ? (
                <img src={logoUrl} alt="" className="w-[46px] h-[22px] object-contain shrink-0" />
              ) : (
                <span className="w-[19px] h-[19px] rounded-[5px] bg-primary text-primary-foreground text-[8.5px] font-extrabold flex items-center justify-center shrink-0">
                  {initials(company.name)}
                </span>
              )}
              <span className="text-[12.5px] font-extrabold text-slate-900 truncate">{company.name}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
